'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { fetchCountries, uploadFile } from '@eliadmin/core/src/api/general/general-api';
import { createWholesaleRegistration } from '@eliadmin/core/src/api/wholesale/wholesale-api';
import { SignInDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import {
  CheckboxField,
  SelectField,
  TextField,
} from '@eliadmin/core/src/components/svetech/forms/fields';
import {
  FormAlert,
  FormSection,
  fieldGridClassName,
  fieldItemClassName,
  formCardClassName,
} from '@eliadmin/core/src/components/svetech/forms/form-parts';
import { FormSuccess } from '@eliadmin/core/src/components/svetech/forms/form-success';
import { Button as SvtButton, ButtonLink } from '@eliadmin/core/src/components/svetech/ui/button';
import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { wholesalerSchema } from '@eliadmin/core/src/components/wholesale/wholesale-schema';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export { wholesalerSchema } from '@eliadmin/core/src/components/wholesale/wholesale-schema';

type WholesaleFormInputValues = z.input<ReturnType<typeof wholesalerSchema>>;
export type WholesaleFormValues = z.output<ReturnType<typeof wholesalerSchema>>;

const eshopOptions = [
  { value: 'eshop', label: 'text298' },
  { value: 'bms', label: 'text299' },
  { value: 'eshop_bms', label: 'text300' },
] as const;

/** Remove button of a repeated row (domain, store address): 50 px square with the close icon. */
function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex size-14 shrink-0 items-center justify-center rounded-xl rounded-tr-none border border-[#e6e8e2] bg-white text-[#b3261e] transition-colors hover:border-[#b3261e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c7a25] focus-visible:ring-offset-2 motion-reduce:transition-none md:size-[50px] md:rounded-[15px] md:rounded-tr-none"
    >
      <Icon name="close" className="size-5" />
    </button>
  );
}

/**
 * Wholesale registration of a signed-in customer (core logic unchanged): sign-in prompt, „already
 * a wholesaler“ and the sent state as Svetech alerts / the confirmation panel; the form in H4
 * sections with the repeated domains and store addresses, the attachment drop zone and the terms.
 */
export function WholesaleForm() {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();

  const me = useCustomerStore(state => state.me);

  const { formValidationMessages } = useFormValidationMessages(t);
  const openDialog = useDialogStore(s => s.open);

  const schema = wholesalerSchema(formValidationMessages);

  const form = useForm<WholesaleFormInputValues, any, WholesaleFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      selectType: 'eshop',
      company: '',
      name: '',
      surname: '',
      ico: '',
      ic_dph: '',
      dic: '',
      address: '',
      street_number: '',
      city: '',
      postcode: '',
      country: {
        id: '',
      },
      phone: '',
      email: me?.customer.email || '',
      files: [],
      domains: [],
      addresses: [],
      tos: false,
    },
  });

  useEffect(() => {
    form.setValue('email', me?.customer.email || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.customer.email]);

  const domainsArray = useFieldArray({
    control: form.control,
    name: 'domains',
  });

  const addressesArray = useFieldArray({
    control: form.control,
    name: 'addresses',
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleFileChange = (file: File | undefined) => {
    if (!file) return;
    setSelectedFiles(prevSelectedFiles => {
      const fileIndex = prevSelectedFiles.findIndex(f => f.name === file.name);

      if (fileIndex >= 0) {
        return prevSelectedFiles.filter((_, index) => index !== fileIndex);
      } else {
        return [...prevSelectedFiles, file];
      }
    });
  };

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });
  const isInitialized = useCustomerStore(state => state.isInitialized);

  const selectTypeValue = useWatch({ control: form.control, name: 'selectType' });
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  /** Form-level message: the core only logged failures other than field errors. */
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = form.handleSubmit(async data => {
    setSubmitError(null);
    form.setValue('files', []);
    const uploadPromises = selectedFiles.map(async file => {
      const formData = new FormData();
      formData.append('file', file);
      return uploadFile({
        authToken: me?.authToken || '',
        formData,
        locale,
      })
        .then(res => {
          return { id: res.data.id };
        })
        .catch(() => {
          return null;
        });
    });

    try {
      const uploadedFiles = await Promise.all(uploadPromises);
      const nonNullUploadedFiles = uploadedFiles.filter(file => file !== null) as { id: number }[];

      const typeId = data.selectType === 'eshop_bms' ? 3 : data.selectType === 'eshop' ? 1 : 2;
      const transformedRequestData = {
        ...data,
        type: {
          id: typeId,
        },
        files: nonNullUploadedFiles,
      };

      await createWholesaleRegistration(transformedRequestData, me?.authToken || '');
      setIsFormSubmitted(true);
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        setSubmitError(t('text4008'));
      } else {
        setSubmitError(t('text320'));
      }
    }
  });

  if (isFormSubmitted) {
    return (
      <div className={formCardClassName}>
        <FormSuccess title={t('text643')} text={t('text774')}>
          <ButtonLink href="/" className="max-md:w-full">
            {t('text725')}
          </ButtonLink>
        </FormSuccess>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div aria-busy="true" aria-label={t('text439')} className="flex flex-col gap-4">
        <Skeleton className="h-8 w-1/2 rounded-lg rounded-tr-none" />
        <Skeleton className="h-14 w-full rounded-xl rounded-tr-none md:h-[50px] md:rounded-[15px] md:rounded-tr-none" />
        <Skeleton className="h-14 w-full rounded-xl rounded-tr-none md:h-[50px] md:rounded-[15px] md:rounded-tr-none" />
      </div>
    );
  }

  if (!me) {
    return (
      <FormAlert
        tone="info"
        action={
          <SvtButton
            onClick={() => {
              openDialog({
                id: SignInDialog.id,
                content: <SignInDialog />,
              });
            }}
          >
            {t('text189')}
          </SvtButton>
        }
      >
        {t('text763')}
      </FormAlert>
    );
  }

  if (me?.customer.is_wholesaler) {
    return <FormAlert tone="success">{t('text765')}</FormAlert>;
  }

  const countryOptions = countries.data.map(c => ({ value: c.id, label: t(c.name as any) }));

  return (
    <Form {...form}>
      <form
        noValidate
        className={cn(formCardClassName, 'flex flex-col gap-8 md:gap-10')}
        onSubmit={handleSubmit}
      >
        <FormSection title={t('text766')}>
          <SelectField
            control={form.control}
            name="selectType"
            label={t('text766')}
            options={eshopOptions.map(option => ({ value: option.value, label: t(option.label) }))}
            required
            hideLabel
            className="md:max-w-[calc(50%-8px)]"
          />

          {selectTypeValue != 'bms' && (
            <div className="flex flex-col gap-4">
              {domainsArray.fields.map((field, i) => (
                <div key={field.id} className="flex items-end gap-3">
                  <TextField
                    control={form.control}
                    name={`domains.${i}.url`}
                    label={t('text325') + ` (${i + 1})`}
                    placeholder={t('text325')}
                    type="url"
                    className="flex-1"
                  />
                  <RemoveButton
                    label={`${t('text325')} (${i + 1})`}
                    onClick={() => domainsArray.remove(i)}
                  />
                </div>
              ))}
              <SvtButton
                variant="text"
                className="self-start px-0"
                onClick={() => domainsArray.append({ url: '' })}
              >
                {t('text326')}
              </SvtButton>
            </div>
          )}
        </FormSection>

        <FormSection title={t('text324')}>
          <div className={fieldGridClassName}>
            <TextField
              control={form.control}
              name="company"
              label={t('text328')}
              placeholder={t('text328')}
              autoComplete="organization"
              required
              className="md:col-span-2"
            />
            <TextField
              control={form.control}
              name="name"
              label={t('text33')}
              placeholder={t('text33')}
              autoComplete="given-name"
              required
            />
            <TextField
              control={form.control}
              name="surname"
              label={t('text34')}
              placeholder={t('text34')}
              autoComplete="family-name"
              required
            />
            <TextField
              control={form.control}
              name="ico"
              label={t('text42')}
              placeholder={t('text42')}
              required
            />
            <TextField
              control={form.control}
              name="ic_dph"
              label={t('text44')}
              placeholder={t('text44')}
              required
            />
            <TextField
              control={form.control}
              name="dic"
              label={t('text43')}
              placeholder={t('text43')}
              required
            />
          </div>
        </FormSection>

        <FormSection title={t('text157')}>
          <div className={fieldGridClassName}>
            <TextField
              control={form.control}
              name="address"
              label={t('text38')}
              placeholder={t('text38')}
              autoComplete="address-line1"
              required
            />
            <TextField
              control={form.control}
              name="street_number"
              label={t('text438')}
              placeholder={t('text438')}
              required
            />
            <TextField
              control={form.control}
              name="city"
              label={t('text39')}
              placeholder={t('text39')}
              autoComplete="address-level2"
              required
            />
            <TextField
              control={form.control}
              name="postcode"
              label={t('text40')}
              placeholder={t('text40')}
              autoComplete="postal-code"
              required
            />
            <SelectField
              control={form.control}
              name="country.id"
              label={t('text41')}
              placeholder={t('text41')}
              disabled={isCountriesLoading}
              options={countryOptions}
              required
            />
          </div>

          {selectTypeValue != 'eshop' && (
            <div className="flex flex-col gap-4">
              {addressesArray.fields.map((field, i) => (
                <div
                  key={field.id}
                  role="group"
                  aria-labelledby={`store-address-${field.id}`}
                  className="flex flex-col gap-4 rounded-3xl rounded-tr-none bg-[#f2f3ef] p-4 md:rounded-[32px] md:rounded-tr-none md:p-6"
                >
                  <div className="flex items-center justify-between gap-4">
                    <p id={`store-address-${field.id}`} className="svt-title-card text-[#14171a]">
                      {t('text330')} {i + 1}
                    </p>
                    <RemoveButton
                      label={`${t('text330')} ${i + 1}`}
                      onClick={() => addressesArray.remove(i)}
                    />
                  </div>
                  <div className={fieldGridClassName}>
                    <TextField
                      control={form.control}
                      name={`addresses.${i}.name`}
                      label={t('text331')}
                      placeholder={t('text331')}
                      required
                      className="md:col-span-2"
                    />
                    <TextField
                      control={form.control}
                      name={`addresses.${i}.address`}
                      label={t('text38')}
                      placeholder={t('text38')}
                      required
                    />
                    {/* Required by the core schema but missing in the core form (unsendable). */}
                    <TextField
                      control={form.control}
                      name={`addresses.${i}.street_number`}
                      label={t('text438')}
                      placeholder={t('text438')}
                      required
                    />
                    <TextField
                      control={form.control}
                      name={`addresses.${i}.city`}
                      label={t('text39')}
                      placeholder={t('text39')}
                      required
                    />
                    <TextField
                      control={form.control}
                      name={`addresses.${i}.postcode`}
                      label={t('text40')}
                      placeholder={t('text40')}
                      required
                    />
                    <SelectField
                      control={form.control}
                      name={`addresses.${i}.country.id`}
                      label={t('text41')}
                      placeholder={t('text41')}
                      options={countryOptions}
                      required
                    />
                  </div>
                </div>
              ))}
              <SvtButton
                variant="text"
                className="self-start px-0"
                onClick={() =>
                  addressesArray.append({
                    name: '',
                    address: '',
                    city: '',
                    street_number: '',
                    postcode: '',
                    country: {
                      id: '',
                    },
                  })
                }
              >
                {t('text329')}
              </SvtButton>
            </div>
          )}
        </FormSection>

        <FormSection title={t('text767')}>
          <div className={fieldGridClassName}>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem required className={fieldItemClassName}>
                  <FormLabel>{t('text36')}</FormLabel>
                  <FormControl>
                    <PhoneInputField {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <TextField
              control={form.control}
              name="email"
              type="email"
              label={t('text35')}
              placeholder={t('text35')}
              disabled={me?.customer.email !== ''}
              required
            />
          </div>
        </FormSection>

        <FormSection title={t('text333')} description={t('text334')}>
          {selectedFiles.length ? (
            <ul className="flex flex-col gap-2">
              {selectedFiles.map(file => (
                <li
                  key={file.name}
                  className="flex items-center gap-3 rounded-xl rounded-tr-none border border-[#e6e8e2] bg-white py-1 pl-4 pr-1 md:rounded-[15px] md:rounded-tr-none"
                >
                  <Icon name="file-text" className="size-5 shrink-0 text-[#2c7a25]" />
                  <span className="svt-body min-w-0 flex-1 truncate text-[#14171a]">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    aria-label={file.name}
                    onClick={() => handleFileChange(file)}
                    className="flex size-11 shrink-0 items-center justify-center rounded-lg text-[#4a5560] transition-colors hover:text-[#b3261e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c7a25] motion-reduce:transition-none"
                  >
                    <Icon name="close" className="size-5" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {/* Drop zone: the whole dashed card is the label of the visually hidden file input. */}
          <label
            htmlFor="file-upload"
            className="flex cursor-pointer flex-col items-center gap-3 rounded-3xl rounded-tr-none border border-dashed border-[#c9cdc4] bg-[#fbfbf9] px-4 py-8 text-center transition-colors hover:border-[#2c7a25] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#2c7a25] has-[:focus-visible]:ring-offset-2 motion-reduce:transition-none md:rounded-[32px] md:rounded-tr-none"
          >
            <span className="flex size-[50px] items-center justify-center rounded-xl rounded-tr-none bg-[#fdf4ce] text-[#2c7a25] md:rounded-[15px] md:rounded-tr-none">
              <Icon name="download" className="size-6 rotate-180" />
            </span>
            <span className="svt-button text-[#14171a]">{t('text335')}</span>
            <span className="svt-body-s text-[#6b7681]">{t('text768')}</span>
            <input
              id="file-upload"
              type="file"
              name="files"
              className="sr-only"
              onChange={e => {
                handleFileChange(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
        </FormSection>

        <div className="flex flex-col gap-6">
          <CheckboxField control={form.control} name="tos" className="ea-register-form__tos-field">
            {t('text336')}
          </CheckboxField>
          {submitError ? <FormAlert tone="error">{submitError}</FormAlert> : null}
          <div className="flex md:justify-end">
            <Button
              type="submit"
              isLoading={form.formState.isSubmitting}
              className="w-full md:w-auto"
            >
              {t('text192')}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
